import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeftRight, X } from "lucide-react";

/**
 * <TradeProposalDialog open listingId onOpenChange onCreated />
 * Lets the current user pick one of their garage vehicles + set a cash delta,
 * then POSTs /api/listings/{id}/trade-proposal.
 */
export default function TradeProposalDialog({ open, onOpenChange, listing, onCreated }) {
    const [vehicles, setVehicles] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [cashDelta, setCashDelta] = useState(0);
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get("/vehicles").then((r) => {
            setVehicles(r.data.items || []);
            if (r.data.items?.length) setSelectedId(r.data.items[0].vehicle_id);
        }).catch((e) => toast.error(formatApiError(e)));
    }, [open]);

    async function submit(e) {
        e.preventDefault();
        if (!selectedId) return toast.error("Pick a vehicle from your garage");
        setBusy(true);
        try {
            const { data } = await api.post(
                `/listings/${listing.listing_id}/trade-proposal`,
                {
                    offered_vehicle_id: selectedId,
                    cash_delta: Number(cashDelta) || 0,
                    message,
                },
            );
            toast.success("Trade proposal sent");
            onOpenChange(false);
            onCreated?.(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg surface" data-testid="trade-proposal-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-white">
                        <ArrowLeftRight className="w-4 h-4 text-hellcat" />
                        Propose a trade
                    </DialogTitle>
                </DialogHeader>

                {vehicles.length === 0 ? (
                    <div className="text-sm text-onyx-500 py-6">
                        Add a vehicle to your Garage first — trades are backed by real cars.
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4" data-testid="trade-proposal-form">
                        <div>
                            <label className="label-caps mb-1 block">Your vehicle</label>
                            <select
                                data-testid="trade-vehicle-select"
                                value={selectedId}
                                onChange={(e) => setSelectedId(e.target.value)}
                                className="w-full px-3 py-2.5 rounded-md bg-onyx-900 border border-white/10 text-sm text-white"
                            >
                                {vehicles.map((v) => (
                                    <option key={v.vehicle_id} value={v.vehicle_id}>
                                        {v.year} {v.model_name} {v.trim || ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="label-caps mb-1 block">Cash delta</label>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-onyx-500">You add $</span>
                                <input
                                    data-testid="trade-cash-input"
                                    type="number"
                                    value={cashDelta}
                                    onChange={(e) => setCashDelta(e.target.value)}
                                    className="flex-1 px-3 py-2.5 rounded-md bg-onyx-900 border border-white/10 text-sm text-white"
                                    placeholder="0"
                                />
                            </div>
                            <p className="text-[11px] text-onyx-500 mt-1">
                                Positive = you add cash · negative = seller adds cash on top
                            </p>
                        </div>
                        <div>
                            <label className="label-caps mb-1 block">Note (optional)</label>
                            <textarea
                                data-testid="trade-message-input"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={3}
                                placeholder="Include mileage/mods/anything worth mentioning"
                                className="w-full px-3 py-2 rounded-md bg-onyx-900 border border-white/10 text-sm text-white"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <button
                                type="button"
                                data-testid="trade-cancel-btn"
                                onClick={() => onOpenChange(false)}
                                className="btn-ghost px-4 py-2 text-sm font-bold inline-flex items-center gap-1"
                            >
                                <X className="w-3.5 h-3.5" /> Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="trade-submit-btn"
                                className="btn-red px-5 py-2 text-sm font-bold"
                            >
                                {busy ? "Sending…" : "Send proposal"}
                            </button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
